import { api } from "../api";
import type { UserInviteCreate, UserListItem, PaginatedResponse, ClassSession } from "../../types/domain";
import type { MeSettingsProfile, MeSettingsProfileUpdate } from "../userPortalService";

export interface UserProfileDetail extends UserListItem {
  gender?: string | null;
  height?: number | null;
  weight?: number | null;
  bio?: string | null;
  parent_phone?: string | null;
  medical_note?: string | null;
}

export const userService = {
  getAll: (params?: {
    role?: string;
    center_id?: string | null;
    search?: string;
    skip?: number;
    limit?: number;
    from?: number;
    to?: number;
    page?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.role) query.set("role", params.role);
    if (params?.center_id) query.set("center_id", params.center_id);
    if (params?.search) query.set("search", params.search);

    // Handle pagination with flexible parameter combinations
    let skipValue: number | undefined;
    let limitValue: number | undefined;

    // Support multiple pagination styles
    if (params?.page !== undefined && params?.limit !== undefined) {
      // Page-based pagination: page=1, limit=50 → convert to skip/limit
      const page = params.page;
      const limit = Math.min(params.limit, 500); // Enforce backend max
      skipValue = (page - 1) * limit;
      limitValue = limit;
    } else if (params?.from !== undefined && params?.to !== undefined) {
      // Offset-based pagination: from=0, to=50 → convert to skip/limit
      skipValue = params.from;
      limitValue = params.to;
    } else if (params?.skip !== undefined && params?.limit !== undefined) {
      // Direct skip/limit
      skipValue = params.skip;
      limitValue = Math.min(params.limit, 500); // Enforce backend max
    } else if (params?.from !== undefined) {
      // Only from provided → use as skip
      skipValue = params.from;
    } else if (params?.limit !== undefined) {
      // Only limit provided → use as limit
      limitValue = Math.min(params.limit, 500); // Enforce backend max
    }

    // Send backend-compatible skip/limit parameters
    if (skipValue !== undefined) query.set("skip", String(skipValue));
    if (limitValue !== undefined) query.set("limit", String(limitValue));

    const qs = query.toString();
    return api.get<PaginatedResponse<UserListItem>>(`/users${qs ? `?${qs}` : ""}`);
  },

  create: (data: UserInviteCreate) => api.post<UserListItem>("/users", data),

  getById: (id: string) => api.get<UserProfileDetail>(`/users/${id}`),

  deactivate: (id: string) =>
    api.patch<{ id: string; is_active: boolean }>(`/users/${id}/deactivate`),

  activate: (id: string) =>
    api.patch<{ id: string; is_active: boolean }>(`/users/${id}/activate`),

  deleteUser: (id: string) => api.delete<void>(`/users/${id}`),

  setPassword: (userId: string, newPassword: string) =>
    api.post(`/users/${userId}/set-password`, { new_password: newPassword }),

  getUserSessions: (userId: string, from: string, to: string, skip = 0, limit = 50) =>
    api.get<PaginatedResponse<ClassSession>>(
      `/users/${userId}/sessions?from_datetime=${from}&to_datetime=${to}&skip=${skip}&limit=${limit}`
    ),

  getSettingsProfile: (userId: string): Promise<MeSettingsProfile> =>
    api.get(`/users/${userId}/settings-profile`),

  updateSettingsProfile: (userId: string, payload: MeSettingsProfileUpdate): Promise<MeSettingsProfile> =>
    api.patch(`/users/${userId}/settings-profile`, payload),

  updateUserRole: (userId: string, role: string) =>
    api.patch<UserListItem>(`/users/${userId}`, { role }),

  getNextUsername: (centerId?: string) => {
    const query = centerId ? `?center_id=${centerId}` : "";
    return api.get<{ username: string | null }>(`/users/next-username${query}`);
  },
};
